import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import moment from 'moment';
import qs from 'qs';
import { getLocale, setLocale } from 'umi';

import { parseJson } from '../utils';
import { getSessionItem, getSessionToken, STORAGE_KEYS } from '../auth';

/**
 * Map of accepted `lang` query values to the locales supported by the platform.
 * Keys are normalized to lower-case before lookup.
 */
const SUPPORTED_LANG_QUERY = {
  en: 'en-US',
  'en-us': 'en-US',
  es: 'es-ES',
  'es-es': 'es-ES',
  'es-pe': 'es-ES',
};

/**
 * Normalize an arbitrary `lang` value (e.g. from a URL) to a supported locale.
 * @param {string} value Raw language value (e.g. 'en', 'es', 'es-PE')
 * @returns {string|null} Supported locale (e.g. 'en-US', 'es-ES') or null when unsupported
 */
export const normalizeLang = (value) => {
  if (!value) return null;
  return SUPPORTED_LANG_QUERY[String(value).trim().toLowerCase()] || null;
};

/**
 * Read the `lang` parameter from a query string and apply it as the active locale.
 * Keeps the platform locale (umi `umi_locale` storage) and moment in sync so the
 * choice persists across the login flow and into the authenticated app.
 * @param {string} [search] Query string (defaults to current location search)
 * @returns {string|null} Applied locale or null when no valid `lang` was provided
 */
export const applyLocaleFromQuery = (search) => {
  try {
    const query = search ?? (typeof window !== 'undefined' ? window.location.search : '');
    const params = qs.parse(query, { ignoreQueryPrefix: true });
    const locale = normalizeLang(params?.lang);

    if (locale && locale !== getLocale()) {
      setLocale(locale, false);
      moment.locale(_.toLower(locale));
    }

    return locale;
  } catch (e) {
    return null;
  }
};

/**
 * Get the default locale from user token, user profile, the active selection,
 * or the browser language.
 *
 * Resolution order:
 *   1. JWT `language` claim (explicit user preference from backend)
 *   2. Stored user profile `defaultLanguage` / `company.language`
 *   3. Active app locale (login selection / `lang` URL param) — only when
 *      `fallbackToCurrent` is enabled, so the login stays in sync with the app
 *   4. Browser language
 *
 * @param {Object} [payload] User auth token payload
 * @param {Object} [options]
 * @param {boolean} [options.fallbackToCurrent=false] Use the active locale before the browser language
 * @returns {string} Locale string (e.g., 'es-PE', 'en-US')
 */
export const getDefaultLocale = (payload = {}, { fallbackToCurrent = false } = {}) => {
  let locale = null;

  try {
    const token = payload ?? getSessionToken();
    locale = jwtDecode(token?.accessToken)?.language;
  } catch (e) {}

  if (!locale) {
    try {
      const user = parseJson(getSessionItem(STORAGE_KEYS.AUTH_USER));
      locale = user?.defaultLanguage ?? user?.company?.language;
    } catch (e) {}

    if (!locale && fallbackToCurrent) {
      try {
        locale = getLocale();
      } catch (e) {}
    }

    if (!locale) {
      const userLanguage = (navigator.language || navigator.languages[0])?.toLowerCase();
      const localeMap = {
        es: 'es-PE',
        en: 'en-US',
      };

      const langCode = userLanguage.split('-')[0];
      return localeMap[langCode] || localeMap.en;
    }
  }

  return locale;
};
